import { useEffect, useMemo, useRef, useState } from "react";
import { useOrderDefaultsStore } from "../../../hooks/useOrderDefaults";
import { buildOtoLegs } from "../../../utils/otoLegs";
import { determineOrderType } from "../../../utils/orderTypeLogic";
import { formatPrice, computeReaisDistance } from "../../../utils/priceUtils";
import { formatNumber, formatVolume } from "../../../utils/numberFormatting";
import { getBookPriceKey } from "../BookByPriceAnnotations";
import { orderModifierForSide } from "./orderModifierSide";
import marketdata from "../../../data/Services/marketdata";

// Order placement preview: while the order modifier (shift/ctrl/cmd) — the same
// one that arms a real shift+click order — is held over a tradeable ladder
// cell, projects the would-be order (entry + OTO legs) onto the ladder.
//
// Elements that land on a rendered ladder row are returned in `previewByPrice`
// and drawn IN PLACE by the SuperDOM row loop, at the correct price. OTO legs
// whose price falls outside the ladder grid have no row to sit on, so they are
// returned in `badge` and shown in a floating badge near the cursor instead.

const TRADE_CELL = ".bbp-ladder__trade-cell";
const ORDER_TYPE_LABEL = {
  LIMIT: "Limite",
  STOPLIMIT: "Stop",
  MARKET: "Mercado",
};

// Resolve the ladder cell under the cursor to a placeable { side, price }.
const resolveTarget = (element) => {
  const cell = element?.closest?.(TRADE_CELL);
  if (!cell) return null;
  const row = cell.closest("tr[data-bbp-price]");
  const price = row ? Number(row.dataset.bbpPrice) : NaN;
  if (!Number.isFinite(price) || price <= 0) return null;
  if (cell.classList.contains("bbp-ladder__col-bid"))
    return { side: "buy", price };
  if (cell.classList.contains("bbp-ladder__col-ask"))
    return { side: "sell", price };
  return null;
};

export const useBookOrderPreview = ({
  tableRef,
  symbol,
  ladderKeys,
  disabled,
}) => {
  const [target, setTarget] = useState(null);

  // Live OTO config — scoped selectors keep re-renders to genuine changes.
  const otoEnabled = useOrderDefaultsStore((s) => s.getOtoEnabled(symbol));
  const lossTicks = useOrderDefaultsStore((s) => s.getOtoLossTicks(symbol));
  const profitTicks = useOrderDefaultsStore((s) => s.getOtoProfitTicks(symbol));
  const quantity = useOrderDefaultsStore((s) => s.getAdjustedQuantity(symbol));

  const pointerRef = useRef({ x: 0, y: 0 });
  const insideRef = useRef(false);
  const disabledRef = useRef(disabled);
  const rafRef = useRef(0);
  const pendingRef = useRef(null);

  useEffect(() => {
    disabledRef.current = disabled;
  }, [disabled]);

  useEffect(() => {
    const table = tableRef.current;
    if (!table) return undefined;

    // Coalesce cursor moves to one update per frame.
    const flush = () => {
      rafRef.current = 0;
      table.classList.toggle("bbp-ladder--armed", !!pendingRef.current);
      setTarget(pendingRef.current);
    };
    const schedule = (next) => {
      pendingRef.current = next;
      if (!rafRef.current) rafRef.current = requestAnimationFrame(flush);
    };
    const evaluate = (x, y, event, element) => {
      pointerRef.current = { x, y };
      if (disabledRef.current) {
        schedule(null);
        return;
      }
      const hit = resolveTarget(element || document.elementFromPoint(x, y));
      // Arm the preview only when the modifier matches the cell's side:
      // Shift = buy column, Ctrl/Cmd = sell column (chart convention; cross
      // combos place nothing). (#344)
      schedule(
        hit && orderModifierForSide(event, hit.side) ? { ...hit, x, y } : null,
      );
    };

    const onMouseMove = (e) => {
      insideRef.current = true;
      evaluate(e.clientX, e.clientY, e, e.target);
    };
    const onLeave = () => {
      insideRef.current = false;
      schedule(null);
    };
    const onMouseDown = () => schedule(null);
    // Pressing/releasing the modifier with the cursor stationary —
    // re-evaluate against the last known pointer position. The keydown/keyup
    // listeners are GLOBAL (window), so they fire on every mounted book; without
    // the `insideRef` guard a book the cursor already left would re-project a
    // phantom order onto its stale pointer row via elementFromPoint. (#363)
    const onKey = (e) => {
      if (!insideRef.current) return;
      const { x, y } = pointerRef.current;
      evaluate(x, y, e, null);
    };

    // PoC #popout: the ladder may live in a popout window — key/blur events
    // land on THAT window, so listen where the table actually is.
    const win = table.ownerDocument?.defaultView ?? window;
    table.addEventListener("mousemove", onMouseMove);
    table.addEventListener("mouseleave", onLeave);
    table.addEventListener("mousedown", onMouseDown);
    win.addEventListener("keydown", onKey);
    win.addEventListener("keyup", onKey);
    win.addEventListener("blur", onLeave);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
      table.classList.remove("bbp-ladder--armed");
      table.removeEventListener("mousemove", onMouseMove);
      table.removeEventListener("mouseleave", onLeave);
      table.removeEventListener("mousedown", onMouseDown);
      win.removeEventListener("keydown", onKey);
      win.removeEventListener("keyup", onKey);
      win.removeEventListener("blur", onLeave);
    };
  }, [tableRef]);

  // Clear immediately when the panel becomes non-interactive mid-hover.
  useEffect(() => {
    if (!disabled) return;
    pendingRef.current = null;
    setTarget(null);
    tableRef.current?.classList.remove("bbp-ladder--armed");
  }, [disabled, tableRef]);

  return useMemo(() => {
    if (!target) return { previewByPrice: null, badge: null };

    const currentPrice = marketdata.getLastQuote?.(symbol)?.C ?? null;
    const orderType = determineOrderType(
      target.side,
      target.price,
      currentPrice,
    );
    const typeLabel = ORDER_TYPE_LABEL[orderType] || orderType;
    const sideLetter = target.side === "buy" ? "C" : "V";
    const sideLabel = target.side === "buy" ? "Compra" : "Venda";
    const qtyText = formatVolume(quantity);

    // Entry — always on the hovered row, so always in-range.
    const elements = [
      {
        kind: "entry",
        side: target.side,
        price: target.price,
        label: `${sideLetter} ${qtyText}`,
        priceText: formatPrice(symbol, target.price),
        distText: typeLabel,
        title: `${sideLabel} ${qtyText} @ ${formatPrice(symbol, target.price)} · ${typeLabel}`,
      },
    ];

    if (otoEnabled) {
      const legs = buildOtoLegs({
        mainOrderId: "preview",
        symbol,
        side: target.side,
        quantity,
        anchorPrice: target.price,
        lossTicks,
        profitTicks,
      });
      legs.forEach((leg) => {
        const kind = leg.type === "profit" ? "profit" : "loss";
        const ticks = kind === "profit" ? profitTicks : lossTicks;
        const kindLabel = kind === "profit" ? "GANHO" : "PERDA";
        const reais = computeReaisDistance(
          symbol,
          target.price,
          leg.price,
          quantity,
        );
        const reaisText =
          reais === undefined
            ? ""
            : ` · R$ ${formatNumber(Math.abs(reais), 2)}`;
        elements.push({
          kind,
          side: leg.side,
          price: leg.price,
          label: `${kind === "profit" ? "G" : "P"} ${ticks}t`,
          priceText: formatPrice(symbol, leg.price),
          distText: `${ticks} tks${reaisText}`,
          title: `${kindLabel} @ ${formatPrice(symbol, leg.price)} · ${ticks} tks${reaisText}`,
        });
      });
    }

    // Split: rows that exist in the ladder are drawn in place; the rest
    // (OTO legs beyond the grid) are collected for the cursor badge.
    const previewByPrice = {};
    const offLadder = [];
    elements.forEach((el) => {
      const key = getBookPriceKey(symbol, el.price);
      if (key && ladderKeys?.has(key)) previewByPrice[key] = el;
      else offLadder.push(el);
    });

    return {
      previewByPrice,
      badge: offLadder.length
        ? { items: offLadder, x: target.x, y: target.y }
        : null,
    };
  }, [
    target,
    symbol,
    ladderKeys,
    otoEnabled,
    lossTicks,
    profitTicks,
    quantity,
  ]);
};
